import { useEffect } from "react";

const SITE_URL = "https://jrprocleaningservice.com";
const SITE_NAME = "JR Pro Cleaning Services";
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/logo.png`;

interface PageMeta {
  title: string;
  description: string;
  /** Path beginning with "/" used for the canonical URL, e.g. "/services/deep-cleaning" */
  path?: string;
  image?: string;
  /** Open Graph type, e.g. "website" (default) or "article" */
  type?: "website" | "article";
  /** Robots directive; defaults to "index, follow". Use "noindex, follow" for pages like 404. */
  robots?: string;
}

function setMetaTag(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Per-route SEO: sets document title, meta description, canonical URL,
 * and Open Graph / Twitter tags for the current page.
 */
export function usePageMeta({ title, description, path, image, type = "website", robots }: PageMeta) {
  useEffect(() => {
    const url = `${SITE_URL}${path ?? window.location.pathname}`;
    const ogImage = image
      ? image.startsWith("http")
        ? image
        : `${SITE_URL}${image}`
      : DEFAULT_OG_IMAGE;

    document.title = title;
    setMetaTag("name", "description", description);
    setMetaTag("name", "robots", robots ?? "index, follow");
    setCanonical(url);

    setMetaTag("property", "og:title", title);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:type", type);
    setMetaTag("property", "og:url", url);
    setMetaTag("property", "og:image", ogImage);
    setMetaTag("property", "og:site_name", SITE_NAME);

    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", title);
    setMetaTag("name", "twitter:description", description);
    setMetaTag("name", "twitter:image", ogImage);
  }, [title, description, path, image, type]);
}
